import { Button, Stack, Typography } from '@mui/material'
import { JourneyCategory } from '@planning/app/api'
import { IRailVisitItem } from '@planning/rt-stores/railVisit/RailVisitItem'
import { useTranslate } from '@tolgee/react'
import { Breadcrumbs, Header, useMinimalsTheme } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { BreadcrumpItem } from 'modules/ui/src/theme/components/breadcrumbs/Breadcrumb'
import { FC, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router'

type Props = {
  railVisitItem: IRailVisitItem
  category: JourneyCategory
  direction: 'load' | 'discharge'
  onSave: () => void
  onCancel: () => void
}

export const RailVisitBulkEditHeader: FC<Props> = observer(
  ({ railVisitItem, category, direction, onSave, onCancel }) => {
    const { t } = useTranslate()
    const navigate = useNavigate()
    const theme = useMinimalsTheme()

    const titleText = railVisitItem.data.name
      ? `${railVisitItem.data.name} - ${t('bulkEdit', 'Bulk edit')}`
      : t('bulkEdit', 'Bulk edit')

    const subtitle = t(category)

    const directionLabel = useMemo(() => {
      if (direction === 'load') {
        return t('loadList', 'Load List')
      }
      return t('dischargeList', 'Discharge List')
    }, [direction, t])

    const getBreadcrumbs = () => {
      if (!railVisitItem) return []

      const basePath = PATHS.visits.railVisits.url
      const railVisitDashboardUrl = `${basePath}/${railVisitItem.id}/dashboard`
      const directionPath = `${basePath}/${railVisitItem.id}/${direction}`
      const railVisitsLabel = t('railVisits', 'Rail visits')
      const visitLabel = railVisitItem.data.name || railVisitItem.id.toString()

      const breadcrumbs: BreadcrumpItem[] = [
        {
          label: railVisitsLabel,
          onClick: () => navigate(basePath),
        },
        {
          label: visitLabel,
          onClick: () => navigate(railVisitDashboardUrl),
        },
        {
          label: directionLabel,
          onClick: () => navigate(directionPath),
        },
        {
          label: t('bulkEdit', 'Bulk edit'),
        },
      ]

      return breadcrumbs
    }

    return (
      <>
        <Helmet>
          <title>{titleText}</title>
        </Helmet>
        <Breadcrumbs items={getBreadcrumbs()} />
        <Header
          position='relative'
          leftRenderOption={
            <Stack flex='none'>
              <Typography variant='h6'>{t('bulkEdit', 'Bulk edit')}</Typography>
            </Stack>
          }
          rightRenderOption={
            <Stack
              flexDirection='row'
              alignItems='center'
              justifyContent='end'
              width='100%'
              gap={2}
            >
              <Button
                variant='text'
                color='inherit'
                onClick={onCancel}
                data-cy='bulk-edit-cancel-btn'
              >
                {t('cancel', 'Cancel')}
              </Button>
              <Button
                variant='contained'
                color='primary'
                data-cy='bulk-edit-save-btn'
                onClick={onSave}
              >
                {t('save', 'Save')}
              </Button>
            </Stack>
          }
        />
      </>
    )
  },
)
